import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getHomePageModulesByType } from 'lib/homepage';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

import type { HomePageModule } from 'shared-types';

type InitialValues = Pick<HomePageModule, 'limit' | 'type'>;

interface Props {
  addedTypes: InitialValues['type'][];
  addModule: (values: InitialValues) => void;
}

const HomePageSettingsModulesAdd = ({ addedTypes, addModule }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<HomePageModule['type']>();

  const modules = getHomePageModulesByType({ messages, serverConfig });

  const availableModules = Object.keys(modules).filter(
    (type: InitialValues['type']) => {
      const module = modules[type];
      return (
        module.isSelectable &&
        (!module.unique || (module.unique && !addedTypes.includes(type)))
      );
    }
  );

  const handleAddModule = () => {
    addModule({
      type: selectedType,
      limit: 4,
      ...(modules[selectedType]?.defaultData
        ? { data: { ...modules[selectedType]?.defaultData } }
        : {})
    });
    handleHide();
  };

  const handleHide = () => {
    setSelectedType(null);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        category="transparent"
        icon="plus"
        isSemiRounded={true}
        onClick={() => setIsOpen(true)}
        size="medium"
        style={{
          border: `2px dashed ${theme.lightGreyDark}`,
          padding: '10px 0',
          width: '100%'
        }}
        text={messages.homePageSettingsModulesSectionAddModule}
      />

      <ModalPortal>
        <Modal
          onHide={handleHide}
          title={messages.homePageSettingsModulesSectionAddModule}
          visible={isOpen}
          width="wide"
        >
          <ModalContent>
            <div className="option-list">
              {availableModules.map((type: InitialValues['type']) => (
                <div
                  className={`option-wrapper ${
                    selectedType === type ? 'active' : ''
                  }`}
                  key={type}
                  onClick={() => setSelectedType(type)}
                >
                  <div className="option">
                    <div className="title">{modules[type].title}</div>
                    <div className="blurb">{modules[type].blurb}</div>
                  </div>

                  <div className="icon">
                    <Icon icon="check-circle" />
                  </div>
                </div>
              ))}

              <style jsx>{`
                .option-list {
                  display: grid;
                  gap: 20px;
                  grid-template-columns: repeat(3, 1fr);
                }
                .option-wrapper {
                  align-items: flex-start;
                  border-radius: 10px;
                  border: 2px dashed ${theme.lightGreyDark};
                  cursor: pointer;
                  display: flex;
                  gap: 10px;
                  padding: 15px;
                  transition: border-style 0.3s;
                }
                .option-wrapper:hover {
                  border: 2px dashed ${theme.lightGreyDarkActive};
                }
                .option-wrapper.active {
                  border: 2px solid #a1a8aa;
                }
                .option {
                  color: ${theme.textColorLight};
                  display: flex;
                  flex-direction: column;
                  flex-grow: 1;
                  justify-content: space-between;
                }
                .icon {
                  color: ${theme.textColor};
                  opacity: 0.3;
                  transition: opacity 0.3s;
                }
                .title {
                  font-size: 1.5rem;
                  font-weight: 700;
                }
                .blurb {
                  font-weight: 400;
                  margin-top: 5px;
                }
                .option-wrapper:hover .option,
                .option-wrapper.active .option {
                  color: ${theme.textColor};
                }
                .option-wrapper:hover .icon,
                .option-wrapper.active .icon {
                  opacity: 1;
                }
              `}</style>
            </div>
          </ModalContent>

          <ModalFooter>
            <Button
              category="light"
              onClick={handleHide}
              text={messages.commonCancel}
            />

            <Button
              category="success"
              disabled={!selectedType}
              onClick={handleAddModule}
              text={messages.commonAdd}
            />
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default HomePageSettingsModulesAdd;
